// Vendors.
import React, { useEffect, useContext } from "react";
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
  useLocation,
} from "react-router-dom";
import { Theme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import Drawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import LockIcon from "@mui/icons-material/Lock";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ReorderIcon from "@mui/icons-material/Reorder";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsIcon from "@mui/icons-material/Settings";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import NavigationIcon from "@mui/icons-material/Navigation";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import EditIcon from "@mui/icons-material/Edit";
import TocIcon from "@mui/icons-material/Toc";
import TodayIcon from "@mui/icons-material/Today";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SchoolIcon from "@mui/icons-material/School";

// Custom.
import { VoRouter, VoAuth, I18n } from "@vocollege/app";
import routes from "pages/routes";
import { VoAppContext } from "services/GlobalState";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawer: {
      width: theme.shape.drawerWidth,
      flexShrink: 0,
    },
    drawerPaper: {
      width: theme.shape.drawerWidth,
    },
    drawerContainer: {
      overflow: "auto",
    },
    nestedParentRoot: {
      "&$nestedParentSelected": {
        backgroundColor: theme.palette.grey[100],
      },
    },
    nestedParentSelected: {},
    nested: {
      paddingLeft: theme.spacing(4),
    },
  }),
);

interface ListItemLinkProps {
  icon?: React.ReactElement;
  primary: string;
  to: string;
  exact?: boolean;
  className?: string;
  listItemButtonClassName?: string;
  visible?: boolean;
}

function ListItemLink(props: ListItemLinkProps) {
  const {
    icon,
    primary,
    to,
    exact,
    className,
    listItemButtonClassName,
    visible,
  } = props;
  const location = useLocation();
  const renderLink = React.useMemo(
    () =>
      React.forwardRef<any, Omit<RouterLinkProps, "to">>((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to],
  );

  if (!visible) {
    return <li></li>;
  }

  return (
    <ListItem disablePadding className={className}>
      <ListItemButton
        selected={exact ? location.pathname === to : VoRouter.pathIsActive(to)}
        component={renderLink}
        className={listItemButtonClassName}
      >
        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
        <ListItemText primary={primary} />
      </ListItemButton>
    </ListItem>
  );
}

const Sidebar: React.FC = () => {
  const classes = useStyles();
  const { state, dispatch } = useContext(VoAppContext);

  // Methods.

  const canAccessSomeAcl = () => {
    return (
      VoAuth.ability.can(
        routes["acl.roles"].abilities[0].action,
        routes["acl.roles"].abilities[0].subject,
      ) ||
      VoAuth.ability.can(
        routes["acl.permissions"].abilities[0].action,
        routes["acl.permissions"].abilities[0].subject,
      )
    );
  };

  const canAccessSomeContent = () => {
    return (
      VoAuth.ability.can("manage", "Group") ||
      VoAuth.ability.can(
        routes["content.articles"].abilities[0].action,
        routes["content.articles"].abilities[0].subject,
      ) ||
      VoAuth.ability.can(
        routes["content.applications"].abilities[0].action,
        routes["content.applications"].abilities[0].subject,
      ) ||
      VoAuth.ability.can(
        routes["content.pages"].abilities[0].action,
        routes["content.pages"].abilities[0].subject,
      ) ||
      VoAuth.ability.can(
        routes["content.books"].abilities[0].action,
        routes["content.books"].abilities[0].subject,
      ) ||
      VoAuth.ability.can(
        routes["content.sections"].abilities[0].action,
        routes["content.sections"].abilities[0].subject,
      ) ||
      VoAuth.ability.can(
        routes["content.events"].abilities[0].action,
        routes["content.events"].abilities[0].subject,
      ) ||
      VoAuth.ability.can(
        routes["content.cards"].abilities[0].action,
        routes["content.cards"].abilities[0].subject,
      ) ||
      VoAuth.ability.can(
        routes["content.educations"].abilities[0].action,
        routes["content.educations"].abilities[0].subject,
      )
    );
  };

  const contentPathIsActive = () => {
    return (
      (state.group && VoRouter.pathIsActive(routes.groups.path)) ||
      VoRouter.pathIsActive(routes.content.path)
    );
  };

  // Effects.

  useEffect(() => {
    dispatch({
      key: "sidebar",
      values: {
        content: contentPathIsActive(),
        acl: VoRouter.pathIsActive(routes.acl.path),
        groups: VoRouter.pathIsActive(routes.groups.path),
        navigation: VoRouter.pathIsActive(routes.navigation.path),
        statistics: VoRouter.pathIsActive(routes.statistics.path),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <Toolbar />
        <div className={classes.drawerContainer}>
          <List>
            <ListItemLink
              to={routes.root.path}
              primary={routes.root.label}
              icon={<HomeIcon />}
              visible={true}
            />

            {canAccessSomeContent() && (
              <ListItem
                disablePadding
                onClick={() =>
                  dispatch({
                    key: "sidebar",
                    values: { content: !state.sidebar.content },
                  })
                }
              >
                <ListItemButton selected={contentPathIsActive()}>
                  <ListItemIcon>
                    <EditIcon />
                  </ListItemIcon>
                  <ListItemText primary={routes.content.label} />
                  {state.sidebar.content ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
            )}
            {canAccessSomeContent() && (
              <Collapse in={state.sidebar.content} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {state.group && (
                    <ListItemLink
                      to={routes["groups.id"].path.replace(
                        ":id",
                        state.group.id,
                      )}
                      exact={true}
                      primary={I18n.get.group.label}
                      icon={<GroupWorkIcon />}
                      listItemButtonClassName={classes.nested}
                      visible={VoAuth.ability.can("manage", "Group")}
                    />
                  )}
                  <ListItemLink
                    to={routes["content.articles"].path}
                    exact={true}
                    primary={routes["content.articles"].label}
                    icon={<AnnouncementIcon />}
                    visible={VoAuth.ability.can(
                      routes["content.articles"].abilities[0].action,
                      routes["content.articles"].abilities[0].subject,
                    )}
                    listItemButtonClassName={classes.nested}
                  />
                  <ListItemLink
                    to={routes["content.pages"].path}
                    exact={true}
                    primary={routes["content.pages"].label}
                    icon={<InsertDriveFileIcon />}
                    visible={VoAuth.ability.can(
                      routes["content.pages"].abilities[0].action,
                      routes["content.pages"].abilities[0].subject,
                    )}
                    listItemButtonClassName={classes.nested}
                  />
                  <ListItemLink
                    to={routes["content.sections"].path}
                    exact={true}
                    primary={routes["content.sections"].label}
                    icon={<TocIcon />}
                    visible={VoAuth.ability.can(
                      routes["content.sections"].abilities[0].action,
                      routes["content.sections"].abilities[0].subject,
                    )}
                    listItemButtonClassName={classes.nested}
                  />
                  <ListItemLink
                    to={routes["content.events"].path}
                    exact={true}
                    primary={routes["content.events"].label}
                    icon={<TodayIcon />}
                    visible={VoAuth.ability.can(
                      routes["content.events"].abilities[0].action,
                      routes["content.events"].abilities[0].subject,
                    )}
                    listItemButtonClassName={classes.nested}
                  />
                  <ListItemLink
                    to={routes["content.cards"].path}
                    exact={true}
                    primary={routes["content.cards"].label}
                    icon={<AccountBoxIcon />}
                    visible={VoAuth.ability.can(
                      routes["content.cards"].abilities[0].action,
                      routes["content.cards"].abilities[0].subject,
                    )}
                    listItemButtonClassName={classes.nested}
                  />
                  <ListItemLink
                    to={routes["content.queues"].path}
                    exact={true}
                    primary={routes["content.queues"].label}
                    icon={<ReorderIcon />}
                    visible={VoAuth.ability.can(
                      routes["content.queues"].abilities[0].action,
                      routes["content.queues"].abilities[0].subject,
                    )}
                    listItemButtonClassName={classes.nested}
                  />
                  <ListItemLink
                    to={routes["content.books"].path}
                    exact={true}
                    primary={routes["content.books"].label}
                    icon={<MenuBookIcon />}
                    visible={VoAuth.ability.can(
                      routes["content.books"].abilities[0].action,
                      routes["content.books"].abilities[0].subject,
                    )}
                    listItemButtonClassName={classes.nested}
                  />
                  <ListItemLink
                    to={routes["content.applications"].path}
                    exact={true}
                    primary={routes["content.applications"].label}
                    icon={<SaveAltIcon />}
                    visible={VoAuth.ability.can(
                      routes["content.applications"].abilities[0].action,
                      routes["content.applications"].abilities[0].subject,
                    )}
                    listItemButtonClassName={classes.nested}
                  />
                  <ListItemLink
                    to={routes["content.educations"].path}
                    exact={true}
                    primary={routes["content.educations"].label}
                    icon={<SchoolIcon />}
                    visible={VoAuth.ability.can(
                      routes["content.educations"].abilities[0].action,
                      routes["content.educations"].abilities[0].subject,
                    )}
                    listItemButtonClassName={classes.nested}
                  />
                </List>
              </Collapse>
            )}
            <ListItemLink
              to={routes.users.path}
              primary={routes.users.label}
              icon={<PeopleIcon />}
              visible={VoAuth.ability.can(
                routes.users.abilities[0].action,
                routes.users.abilities[0].subject,
              )}
            />
            {canAccessSomeAcl() && (
              <ListItem
                disablePadding
                onClick={() =>
                  dispatch({
                    key: "sidebar",
                    values: { acl: !state.sidebar.acl },
                  })
                }
              >
                <ListItemButton
                  selected={VoRouter.pathIsActive(routes.acl.path)}
                >
                  <ListItemIcon>
                    <LockIcon />
                  </ListItemIcon>
                  <ListItemText primary={routes.acl.label} />
                  {state.sidebaracl ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
            )}
            {canAccessSomeAcl() && (
              <Collapse in={state.sidebar.acl} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemLink
                    to={routes["acl.roles"].path}
                    exact={true}
                    primary={routes["acl.roles"].label}
                    icon={<PermIdentityIcon />}
                    listItemButtonClassName={classes.nested}
                    visible={VoAuth.ability.can(
                      routes["acl.roles"].abilities[0].action,
                      routes["acl.roles"].abilities[0].subject,
                    )}
                  />
                  <ListItemLink
                    to={routes["acl.permissions"].path}
                    exact={true}
                    primary={routes["acl.permissions"].label}
                    icon={<VpnKeyIcon />}
                    listItemButtonClassName={classes.nested}
                    visible={VoAuth.ability.can(
                      routes["acl.permissions"].abilities[0].action,
                      routes["acl.permissions"].abilities[0].subject,
                    )}
                  />
                </List>
              </Collapse>
            )}
            {VoAuth.ability.can(
              routes.groups.abilities[0].action,
              routes.groups.abilities[0].subject,
            ) && (
              <ListItem
                disablePadding
                onClick={() =>
                  dispatch({
                    key: "sidebar",
                    values: { groups: !state.sidebar.groups },
                  })
                }
              >
                <ListItemButton
                  selected={VoRouter.pathIsActive(routes.groups.path)}
                >
                  <ListItemIcon>
                    <GroupWorkIcon />
                  </ListItemIcon>
                  <ListItemText primary={routes.groups.label} />
                  {state.sidebar.groups ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
            )}
            {VoAuth.ability.can(
              routes.groups.abilities[0].action,
              routes.groups.abilities[0].subject,
            ) && (
              <Collapse in={state.sidebar.groups} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemLink
                    to={routes.groups.path}
                    exact={true}
                    primary={routes.groups.label}
                    icon={<GroupWorkIcon />}
                    listItemButtonClassName={classes.nested}
                    visible={VoAuth.ability.can(
                      routes.groups.abilities[0].action,
                      routes.groups.abilities[0].subject,
                    )}
                  />
                  <ListItemLink
                    to={routes["groups.roles"].path}
                    exact={true}
                    primary={routes["groups.roles"].label}
                    icon={<PermIdentityIcon />}
                    listItemButtonClassName={classes.nested}
                    visible={VoAuth.ability.can(
                      routes["groups.roles"].abilities[0].action,
                      routes["groups.roles"].abilities[0].subject,
                    )}
                  />
                  <ListItemLink
                    to={routes["groups.permissions"].path}
                    exact={true}
                    primary={routes["groups.permissions"].label}
                    icon={<VpnKeyIcon />}
                    listItemButtonClassName={classes.nested}
                    visible={VoAuth.ability.can(
                      routes["groups.permissions"].abilities[0].action,
                      routes["groups.permissions"].abilities[0].subject,
                    )}
                  />
                </List>
              </Collapse>
            )}
            {VoAuth.ability.can(
              routes["navigation.menus"].abilities[0].action,
              routes["navigation.menus"].abilities[0].subject,
            ) && (
              <ListItem
                disablePadding
                onClick={() =>
                  dispatch({
                    key: "sidebar",
                    values: { navigation: !state.sidebar.navigation },
                  })
                }
              >
                <ListItemButton
                  selected={VoRouter.pathIsActive(routes.navigation.path)}
                >
                  <ListItemIcon>
                    <NavigationIcon />
                  </ListItemIcon>
                  <ListItemText primary={routes.navigation.label} />
                  {state.sidebar.navigation ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
            )}
            {VoAuth.ability.can(
              routes["navigation.menus"].abilities[0].action,
              routes["navigation.menus"].abilities[0].subject,
            ) && (
              <Collapse
                in={state.sidebar.navigation}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  <ListItemLink
                    to={routes["navigation.menus"].path}
                    exact={true}
                    primary={routes["navigation.menus"].label}
                    icon={<MenuOpenIcon />}
                    listItemButtonClassName={classes.nested}
                    visible={VoAuth.ability.can(
                      routes["navigation.menus"].abilities[0].action,
                      routes["navigation.menus"].abilities[0].subject,
                    )}
                  />
                </List>
              </Collapse>
            )}

            {VoAuth.ability.can(
              routes["statistics.general"].abilities[0].action,
              routes["statistics.general"].abilities[0].subject,
            ) && (
              <ListItem
                disablePadding
                onClick={() =>
                  dispatch({
                    key: "sidebar",
                    values: { statistics: !state.sidebar.statistics },
                  })
                }
              >
                <ListItemButton
                  selected={VoRouter.pathIsActive(routes.statistics.path)}
                >
                  <ListItemIcon>
                    <LeaderboardIcon />
                  </ListItemIcon>
                  <ListItemText primary={routes.statistics.label} />
                  {state.sidebar.statistics ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
            )}

            {VoAuth.ability.can(
              routes["statistics.general"].abilities[0].action,
              routes["statistics.general"].abilities[0].subject,
            ) && (
              <Collapse
                in={state.sidebar.statistics}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  <ListItemLink
                    to={routes["statistics.general"].path}
                    exact={true}
                    primary={routes["statistics.general"].label}
                    icon={<QueryStatsIcon />}
                    listItemButtonClassName={classes.nested}
                    visible={VoAuth.ability.can(
                      routes["statistics.general"].abilities[0].action,
                      routes["statistics.general"].abilities[0].subject,
                    )}
                  />
                  <ListItemLink
                    to={routes["statistics.certifications"].path}
                    exact={true}
                    primary={routes["statistics.certifications"].label}
                    icon={<PlaylistAddCheckIcon />}
                    listItemButtonClassName={classes.nested}
                    visible={VoAuth.ability.can(
                      routes["statistics.certifications"].abilities[0].action,
                      routes["statistics.certifications"].abilities[0].subject,
                    )}
                  />
                </List>
              </Collapse>
            )}
            <ListItemLink
              to={routes.docs.path}
              primary={routes.docs.label}
              icon={<StorageIcon />}
              visible={VoAuth.ability.can(
                routes.docs.abilities[0].action,
                routes.docs.abilities[0].subject,
              )}
            />

            <ListItemLink
              to={routes.settings.path}
              primary={routes.settings.label}
              icon={<SettingsIcon />}
              visible={VoAuth.ability.can(
                routes.settings.abilities[0].action,
                routes.settings.abilities[0].subject,
              )}
            />
          </List>
          <Divider />
        </div>
      </Drawer>
    </>
  );
};

export default Sidebar;
